import { mapFromGroup, mapFromGroups, mapFromTeamChannels, mapToGroup } from 'mappings/groupMapping';
import GroupDTO from 'models/dto/groupDTO';
import IdListDTO from 'models/dto/IdListDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Group from 'models/group';
import { TeamsChannel } from 'models/teamsChannel';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetGroup(
  groupId: string,
  accessToken: string,
  cache: GlobalDataCache | undefined,
): Promise<Group | undefined> {
  try {
    if (!groupId) return undefined;
    const ar = await http.get<GroupDTO>(`/groups/${groupId}`, http.getRequestConfig(accessToken));

    if (!ar.data) {
      return undefined;
    }

    return mapFromGroup(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetGroups(accessToken: string, cache: GlobalDataCache | undefined): Promise<Group[]> {
  try {
    const ar = await http.get<GroupDTO[]>(`/groups`, http.getRequestConfig(accessToken));

    if (!ar.data) {
      return [];
    }

    return mapFromGroups(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetDirectoryGroups(searchText: string | undefined, accessToken: string): Promise<Group[]> {
  try {
    if (!searchText) {
      searchText = '';
    }

    const ar = await http.get<GroupDTO[]>(`/groups/directory?search=${encodeURIComponent(searchText)}`, http.getRequestConfig(accessToken));

    if (!ar.data) {
      return [];
    }

    return mapFromGroups(ar.data, undefined);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddGroup(accessToken: string, group: Group): Promise<Group | undefined> {
  try {
    const groupDTO = mapToGroup(group);
    const ar = await http.post<GroupDTO>('/groups', groupDTO, http.getRequestConfig(accessToken));

    return mapFromGroup(ar.data, undefined);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateGroup(accessToken: string, group: Group): Promise<Group | undefined> {
  try {
    const groupDTO = mapToGroup(group);
    const ar = await http.put<GroupDTO>('/groups', groupDTO, http.getRequestConfig(accessToken));

    return mapFromGroup(ar.data, undefined);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveGroups(accessToken: string, groupIds: string[]): Promise<void> {
  try {
    const idListDTO: IdListDTO = new IdListDTO(groupIds);
    await http.post<IdListDTO>('/groups/delete', idListDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetChannelsForGroup(groupId: string, accessToken: string): Promise<TeamsChannel[]> {
  try {
    const ar = await http.get<TeamsChannel[]>(`/groups/${groupId}/channels`, http.getRequestConfig(accessToken));

    //handle 204 NoContent for AD groups that have no Team
    if (!ar.data) {
      return [];
    }

    return mapFromTeamChannels(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGroupSync(accessToken: string): Promise<void> {
  try {
    await http.put(`/groups/sync`, undefined, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiTestGroupSync(accessToken: string): Promise<boolean> {
  try {
    const res = await http.get<boolean>(`/groups/sync/test`, http.getRequestConfig(accessToken));

    return res.data;
  } catch {
    return false;
  }
}
